import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RetrievalModePreferenceService } from '../../../services/retrieval-mode-preference/retrieval-mode-preference.service';

const VIEWPORT_BOTTOM_RATIO = 0.55;

@Component({
  selector: 'app-retrieval-mode-toggle',
  templateUrl: './retrieval-mode-toggle.component.html',
  styleUrls: ['./retrieval-mode-toggle.component.scss']
})
export class RetrievalModeToggleComponent implements OnInit, AfterViewInit, OnDestroy {
  /** When true (v3 regulations agent), renders pill label + separate info tooltip button. */
  @Input() showLabel = false;

  private readonly preferenceService = inject(RetrievalModePreferenceService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private readonly onViewportChange = (): void => {
    this.updateTooltipPlacement();
    this.cdr.markForCheck();
  };

  allVersionsEnabled = false;
  tooltipOnTop = true;

  get tooltipKey(): string {
    return this.allVersionsEnabled
      ? 'RETRIEVAL_MODE_TOGGLE_TOOLTIP_ON'
      : 'RETRIEVAL_MODE_TOGGLE_TOOLTIP_OFF';
  }

  get toggleLabelKey(): string {
    return this.allVersionsEnabled
      ? 'RETRIEVAL_MODE_TOGGLE_LABEL_ALL'
      : 'RETRIEVAL_MODE_TOGGLE_LABEL_LATEST';
  }

  ngOnInit(): void {
    this.allVersionsEnabled = this.preferenceService.isAllVersionsEnabled();
    this.preferenceService.allVersionsEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.allVersionsEnabled = value;
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.updateTooltipPlacement();
      this.observePromptChanges();
      this.cdr.markForCheck();
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onViewportChange);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onViewportChange);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onToggle(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.preferenceService.setAllVersionsEnabled(input.checked);
  }

  private observePromptChanges(): void {
    const textarea = this.findPromptTextarea();
    if (textarea && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateTooltipPlacement();
        this.cdr.markForCheck();
      });
      this.resizeObserver.observe(textarea);
    }

    const promptSection = this.findPromptSection();
    if (promptSection && typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => {
        this.updateTooltipPlacement();
        this.cdr.markForCheck();
      });
      this.mutationObserver.observe(promptSection, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  private updateTooltipPlacement(): void {
    const switchEl = this.elementRef.nativeElement.querySelector(
      '.retrieval-mode-info-btn, .retrieval-mode-switch'
    ) as HTMLElement | null ?? this.elementRef.nativeElement;
    const rect = switchEl.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = typeof window !== 'undefined' ? window.innerHeight - rect.bottom : 0;

    const promptSection = this.findPromptSection();
    const agentWrapper = this.elementRef.nativeElement.closest('.agent-prompt-wrapper') as HTMLElement | null;
    const textarea = this.findPromptTextarea();

    const promptAtBottom =
      !!promptSection?.classList.contains('collapsed') ||
      !!promptSection?.classList.contains('new-session') ||
      !!agentWrapper?.classList.contains('prompt-collapsed') ||
      !!textarea?.classList.contains('scrollable-prompt-ready');

    const nearViewportBottom =
      typeof window !== 'undefined' &&
      rect.bottom > window.innerHeight * VIEWPORT_BOTTOM_RATIO;

    this.tooltipOnTop = promptAtBottom || nearViewportBottom || spaceAbove >= spaceBelow;
  }

  private findPromptSection(): HTMLElement | null {
    return this.elementRef.nativeElement.closest('.prompt-section, .prompt-section-new');
  }

  private findPromptTextarea(): HTMLElement | null {
    const root = this.findPromptSection() ?? this.elementRef.nativeElement.closest('.input-wrapper, .form-input');
    return root?.querySelector('.form-prompt') as HTMLElement | null;
  }
}
